import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { reportsApi, proposalsApi } from '../utils/api';

const BAND_COLOR = {
  Strong: '#2f7d4f', Moderate: '#a9711a', 'At Risk': '#d35400', Critical: '#c0392b',
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch (e) { return iso; }
}

/**
 * The client's security assessment reports, each deep-linking to the full
 * report. Extracted from the old standalone /reports page so it can live as a
 * tab inside the merged HIPAA page — compliance status and the assessments
 * that back it belong in one place.
 */
export default function SecurityReportsList() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Pending compliance (SRA) proposal, if any — drives the empty-state CTA
  // pointing a client with no reports yet toward the assessment.
  const [sraProposal, setSraProposal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await reportsApi.list();
        if (!cancelled) setReports(d.reports || []);
      } catch (e) {
        if (!cancelled) setError('Could not load your reports.');
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Best-effort: surface an actionable compliance proposal for the CTA.
      try {
        const proposals = await proposalsApi.list();
        if (cancelled) return;
        const pending = (proposals || []).find(
          (p) => p.path_type === 'compliance'
            && (p.current_stage === 'proposal' || p.current_stage === 'declined')
        );
        setSraProposal(pending || null);
      } catch (e) { /* CTA is optional — ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <p style={{ color: '#777', fontSize: 14, margin: '0 0 20px' }}>
        Your security assessments — what we found and how your protections are holding up.
      </p>

      {loading && <div style={{ padding: 30, color: '#888' }}>Loading…</div>}
      {error && <div style={{ padding: 30, color: '#c0392b' }}>{error}</div>}

      {!loading && !error && reports.length === 0 && (
        <div style={{ padding: 30, background: '#fff', borderRadius: 10, textAlign: 'center' }}>
          <ShieldCheck size={40} color="#068dac" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 6 }}>
            No security reports yet
          </div>
          {sraProposal ? (
            <>
              <p style={{ color: '#777', fontSize: 14, margin: '0 0 16px' }}>
                Your reports start with a HIPAA Security Risk Assessment — and
                you have a proposal waiting for review.
              </p>
              <button
                onClick={() => navigate(`/proposal/${sraProposal.id}`)}
                style={{
                  background: '#068dac', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Review Your Assessment Proposal
              </button>
            </>
          ) : (
            <p style={{ color: '#777', fontSize: 14, margin: 0 }}>
              A HIPAA Security Risk Assessment is the first step — it shows
              exactly where your protections stand and what needs attention.
              Ask us about scheduling yours, and your reports will appear here.
            </p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {reports.map((r) => (
          <button
            key={r.id}
            onClick={() => navigate(`/report/${r.id}`)}
            style={{
              display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
              background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '14px 16px',
              cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,.05)',
            }}
          >
            <div style={{
              width: 52, height: 52, borderRadius: 8, flex: 'none',
              background: BAND_COLOR[r.band] || '#068dac', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, fontWeight: 800,
            }}>{r.score != null ? r.score : '—'}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{r.title || 'Security Assessment'}</div>
              <div style={{ fontSize: 13, color: '#888' }}>
                {fmtDate(r.generated_at)}{r.band ? ` · ${r.band}` : ''}{!r.viewed ? ' · New' : ''}
              </div>
            </div>
            <div style={{ color: '#068dac', fontSize: 20 }}>›</div>
          </button>
        ))}
      </div>
    </>
  );
}
